import { graphql, StaticQuery } from 'gatsby';
import * as React from 'react';

import { ExpandMore } from '@material-ui/icons';

import {
  Button,
  Collapse,
  createStyles,
  Divider,
  Theme,
  Typography,
  withStyles,
  WithStyles,
} from '@nelnet/unifi-components-react';

import PropertyTable from './PropertyTable';

const styles = ({ spacing }: Theme) =>
  createStyles({
    collapseWrap: {
      maxWidth: '1200px',
      marginLeft: `-${spacing(1)}px`,
    },
    collapseHeading: {
      margin: '0 !important',
    },
    collapseHandle: {
      textTransform: 'none',
      fontSize: 'inherit',
      letterSpacing: '0',
      color: 'inherit',
      display: 'flex',
      width: '100%',
      maxWidth: 'calc(100vw - 48px)',
      justifyContent: 'space-between',
      padding: spacing(1),
      border: '1px solid transparent',
      '&:focus': {
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0,0,0,0.37)',
        boxShadow: 'none',
      },
    },
    collapseContent: {
      paddingLeft: spacing(1),
    },
  });

interface IJsonProps {
  node: {
    name: string;
    childPropertydataJson: {
      component: string;
      status: [
        {
          platform: string;
          since?: string;
          stability: string;
        },
      ];
      react: {
        props: [
          {
            name: string;
            type: string;
            default?: string;
            description?: string;
          },
        ];
        css: [
          {
            name: string;
            description?: string;
          },
        ];
      };
      web: {
        classes: [
          {
            class: string;
            description?: string;
          },
        ];
        tokens: [
          {
            variable: string;
            default?: string;
            description?: string;
          },
        ];
        sass: [
          {
            variable: string;
            default?: string;
            description?: string;
          },
        ];
      };
    };
  };
}

interface IPropertiesProps extends WithStyles<typeof styles> {
  components: string;
  platform: string;
  componentData?: IJsonProps[];
}

const CreatePropertyPanels: React.FunctionComponent<IPropertiesProps> = ({
  classes,
  components,
  platform,
  componentData,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-types
  const [expanded, updateExpanded] = React.useState<{}[]>([]);

  const currPlatform: string | 'angular' | 'react' | 'web' = platform;
  const currPlatformName =
    currPlatform.length && currPlatform.charAt(0).toUpperCase() + currPlatform.slice(1);
  const componentsArr = components ? components.split('|') : [];

  const componentList =
    componentData &&
    componentData.filter((data) => {
      // eslint-disable-next-line @typescript-eslint/ban-types
      let dataCheck: {} =
        data.node.childPropertydataJson.react &&
        (data.node.childPropertydataJson.react.props || data.node.childPropertydataJson.react.css);
      if (currPlatform === 'web') {
        dataCheck =
          data.node.childPropertydataJson.web &&
          (data.node.childPropertydataJson.web.classes ||
            data.node.childPropertydataJson.web.tokens ||
            data.node.childPropertydataJson.web.sass);
      }
      return componentsArr.includes(data.node.name) && dataCheck;
    });

  if (currPlatform && componentList.length) {
    let orderedProperties = [] as IJsonProps[];

    componentsArr.forEach((key) => {
      let found = false;
      componentList.filter((item) => {
        if (!found && item.node.name === key) {
          orderedProperties = [...orderedProperties, item];
          found = true;
          return false;
        }
        return true;
      });
    });

    return (
      <>
        <Typography id="properties" variant="h2" margin={{ top: 6, bottom: 2 }}>
          Properties
        </Typography>

        {orderedProperties.map((row) => {
          const data = row.node.childPropertydataJson;
          const { component, status } = data;

          const platformData = status.filter((props) => props.platform === currPlatformName);

          const rotateIcon = expanded.includes(component) ? 'rotate(180deg)' : 'rotate(0)';

          const isExpanded = expanded.includes(component);

          return (
            <div key={component} className={classes.collapseWrap}>
              <Typography variant="h3" className={classes.collapseHeading}>
                <Button
                  variant="text"
                  color="neutral"
                  aria-expanded={isExpanded}
                  onClick={() =>
                    updateExpanded(
                      isExpanded
                        ? expanded.filter((item) => item !== component)
                        : [...expanded, component],
                    )
                  }
                  className={classes.collapseHandle}
                  disableRipple
                >
                  {`<${component} />`}
                  <ExpandMore aria-label="Show more" style={{ transform: rotateIcon }} />
                </Button>
              </Typography>
              <Collapse
                in={isExpanded}
                unmountOnExit
                timeout="auto"
                className={classes.collapseContent}
              >
                <Typography
                  color="textSecondary"
                  variant="subtitle2"
                  margin={{ top: 1 }}
                  gutterBottom
                >
                  {platformData[0].since
                    ? `Since ${platformData[0].since}`
                    : platformData[0].stability}
                </Typography>

                {currPlatform === 'web' && row.node.childPropertydataJson.web.classes && (
                  <PropertyTable
                    title="Classes"
                    headers={['Class', 'Description']}
                    colWidths={['25em', 'auto']}
                    cellValues={row.node.childPropertydataJson.web.classes}
                  />
                )}
                {currPlatform === 'web' && row.node.childPropertydataJson.web.tokens && (
                  <PropertyTable
                    title="Tokens"
                    topMargin={{ top: 4 }}
                    headers={['Variable', 'Default', 'Description']}
                    colWidths={['25em', '15em', 'auto']}
                    cellValues={row.node.childPropertydataJson.web.tokens}
                  />
                )}
                {currPlatform === 'web' && row.node.childPropertydataJson.web.sass && (
                  <PropertyTable
                    title="Variables"
                    topMargin={{ top: 4 }}
                    headers={['Variable', 'Default', 'Description']}
                    colWidths={['25em', '15em', 'auto']}
                    cellValues={row.node.childPropertydataJson.web.sass}
                  />
                )}
                {currPlatform === 'react' && row.node.childPropertydataJson.react.props && (
                  <PropertyTable
                    title="Props"
                    headers={['Name', 'Type', 'Default', 'Description']}
                    colWidths={['20em', '20em', '10em', 'auto']}
                    cellValues={row.node.childPropertydataJson.react.props}
                  />
                )}
                {currPlatform === 'react' && row.node.childPropertydataJson.react.css && (
                  <PropertyTable
                    title="CSS"
                    topMargin={{ top: 4 }}
                    headers={['Class', 'Description']}
                    colWidths={['20em', 'auto']}
                    cellValues={row.node.childPropertydataJson.react.css}
                  />
                )}
              </Collapse>
              <Divider margin={{ y: 1 }} />
            </div>
          );
        })}
      </>
    );
  }

  return null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PropertyPanels = withStyles(styles)((props: IPropertiesProps) => (
  <StaticQuery
    query={graphql`
      query {
        allFile(filter: { extension: { eq: "json" } }) {
          edges {
            node {
              name
              childPropertydataJson {
                component
                status {
                  platform
                  since
                  stability
                }
                react {
                  props {
                    name
                    type
                    default
                    description
                  }
                  css {
                    name
                    description
                  }
                }
                web {
                  classes {
                    class
                    description
                  }
                  tokens {
                    variable
                    default
                    description
                  }
                  sass {
                    variable
                    default
                    description
                  }
                }
              }
            }
          }
        }
      }
    `}
    render={(data) => <CreatePropertyPanels componentData={data.allFile.edges} {...props} />}
  />
));

export default PropertyPanels;
